"use strict";

import { TASK_STATUS } from "./taskStatuses.js";
import {
    formatRequestTime,
    getVideoIngestionState,
    VIDEO_INGESTION_STATE,
} from "./videoIngestion.js";

// Which earlier task each admin action needs, mirroring the backend's
// audio_dashboard_backend/utils/task-prerequisites.js so buttons explain why
// they are disabled instead of failing with a 409.

const running = (status, message) => (status === TASK_STATUS.inProgress ? message : null);

const hasDialogueAudio = (project = {}) =>
    Boolean(
        project.dialogueAudioObjectName ||
        project.dialogueAudioUrl ||
        (project.uploadedAudioTypes || []).some(
            (entry) => entry?.audioType === "dialogue" && (entry.objectName || entry.url)
        )
    );

const hasVoices = (project = {}) =>
    (project.characters || []).some(
        (character) => Array.isArray(character?.voices) && character.voices.length > 0
    );

// Projects translated before translationSources existed only have the status.
const isTranslated = (project = {}, translationStatus, language) => {
    const sources = project.translationSources;
    if (sources && Object.keys(sources).length > 0) {
        return Boolean(sources[language]);
    }
    return translationStatus === TASK_STATUS.completed;
};

// Projects dubbed before task statuses existed have no voiceGeneration status,
// but their earlier versions show the takes were created.
const hasDubbedVersions = (project = {}) =>
    (project.dubbedAudiosList || []).length > 0 || (project.dubbedVideosList || []).length > 0;

// Takes are also fixed one by one on the performance direction page, which
// never marks the batch completed, so only a batch that never ran blocks.
const takesBlocker = (statuses, project) =>
    running(statuses.voiceGeneration, "Wait for the takes to finish") ||
    (statuses.voiceGeneration === TASK_STATUS.notStarted && !hasDubbedVersions(project)
        ? "Create takes first"
        : null);

// How each running task is named in the reload-video blocker, in pipeline order.
const RELOAD_VIDEO_RUNNING_TASKS = [
    ["stemSeparation", "stem separation"],
    ["transcription", "transcription"],
    ["nonverbalDetection", "non-verbal detection"],
    ["translation", "translation"],
    ["audioChopping", "audio chopping"],
    ["voiceTraining", "voice training"],
    ["voiceGeneration", "the takes"],
    ["lipSync", "lip sync"],
    ["merging", "the remux"],
];

// A new video restarts the pipeline, so nothing may be running, including an
// earlier video that is still loading. Transcription keeps a project's existing
// dialogue lines (a finished run is a no-op for a new source), so a project
// that already has lines cannot take a new video.
export const reloadVideoBlocker = (statuses, project = {}, now = Date.now()) => {
    const runningTask = RELOAD_VIDEO_RUNNING_TASKS.find(
        ([key]) => statuses[key] === TASK_STATUS.inProgress
    );
    if (runningTask) {
        return `Wait for ${runningTask[1]} to finish`;
    }
    const ingestion = getVideoIngestionState(project, now);
    if (ingestion?.state === VIDEO_INGESTION_STATE.loading) {
        return `A new video is still loading (requested ${formatRequestTime(
            ingestion.requestedAt,
            new Date(now)
        )}). Wait for it to finish before uploading another.`;
    }
    if (
        statuses.transcription === TASK_STATUS.completed ||
        Number(project?.numberOfDialogues) > 0
    ) {
        return "This project already has dialogue lines, which a new video would not replace. Create a new project for the new video.";
    }
    return null;
};

// `statuses` uses the admin actions state keys; returns why each action cannot
// start yet, or null when it can.
export const getActionBlockers = ({
    statuses,
    project,
    takesLanguage,
    languageName = (language) => language,
}) => ({
    transcription:
        running(statuses.transcription, "Transcription is already running") ||
        running(statuses.stemSeparation, "Wait for stem separation to finish") ||
        (hasDialogueAudio(project) ? null : "Separate the stems first"),

    // Reads only the dialogue stem and the lines, so projects transcribed
    // before word timings were saved can run it too.
    nonverbalDetection:
        running(statuses.nonverbalDetection, "Non-verbal detection is already running") ||
        running(statuses.transcription, "Wait for transcription to finish") ||
        (hasDialogueAudio(project) ? null : "Separate the stems first"),

    translation:
        running(statuses.translation, "Translation is already running") ||
        running(statuses.transcription, "Wait for transcription to finish") ||
        running(statuses.nonverbalDetection, "Wait for non-verbal detection to finish") ||
        (statuses.transcription === TASK_STATUS.completed || Number(project?.numberOfDialogues) > 0
            ? null
            : "Transcribe the project first"),

    voiceTraining:
        running(statuses.voiceTraining, "Voice training is already running") ||
        running(statuses.audioChopping, "Wait for audio chopping to finish") ||
        // Existing voices mean chopping already ran, even on older projects.
        (statuses.audioChopping === TASK_STATUS.completed || hasVoices(project)
            ? null
            : "Chop the audio first"),

    voiceGeneration:
        running(statuses.voiceGeneration, "Takes are already being created") ||
        running(statuses.translation, "Wait for translation to finish") ||
        running(statuses.voiceTraining, "Wait for voice training to finish") ||
        (!takesLanguage || isTranslated(project, statuses.translation, takesLanguage)
            ? null
            : `Translate to ${languageName(takesLanguage)} first`) ||
        (hasVoices(project) ? null : "Train voices first"),

    lipSync:
        running(statuses.lipSync, "Lip sync is already running") || takesBlocker(statuses, project),

    remux: running(statuses.merging, "A remux is already running") || takesBlocker(statuses, project),

    reloadVideo: reloadVideoBlocker(statuses, project),
});
